import { memo, useEffect, useMemo, useState } from "react"
import { createFileRoute, Link, notFound } from "@tanstack/react-router"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { useTheme } from "next-themes"
import {
  PencilSimple,
  ArrowSquareOut,
  ClockCounterClockwise,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react"
import { CopyableCode } from "~/components/design-system/components"
import { readMarkdownCode } from "~/utils/markdownCode"
import { cn } from "~/utils/helpers"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { docsBySlug, resolveDocLink, headingId, docsByFilename, type Doc } from "~/utils/docs"

const GITHUB_EDIT_BASE = "https://github.com/hakio-aps/hakio_core/edit/main/frontend_api/docs"
const GITHUB_COMMIT_BASE = "https://github.com/hakio-aps/hakio_core/commit"

const ARTIFACT_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox"

const SCROLLSPY_HEADER_OFFSET = 80

const HEADING_STYLES: Record<2 | 3 | 4, string> = {
  2: "text-xl font-semibold mt-8 mb-3",
  3: "text-lg font-semibold mt-6 mb-2",
  4: "text-base font-semibold mt-4 mb-2",
}

export const Route = createFileRoute("/_auth/internal/docs/_layout/$slug")({
  loader: ({ params }) => {
    const doc = docsBySlug.get(params.slug)
    if (!doc) throw notFound()
    return { doc }
  },
  component: DocPage,
  notFoundComponent: () => (
    <div className="max-w-3xl mx-auto px-8 py-10">
      <h1 className="text-xl font-semibold mb-2">Doc not found</h1>
      <p className="text-fg-subtle">
        <Link to="/internal/docs" className="underline">
          Back to docs
        </Link>
      </p>
    </div>
  ),
})

const childrenToText = (children: React.ReactNode): string => {
  if (typeof children === "string") return children
  if (typeof children === "number") return String(children)
  if (Array.isArray(children)) return children.map(childrenToText).join("")
  if (children && typeof children === "object" && "props" in children) {
    return childrenToText((children as { props: { children?: React.ReactNode } }).props.children)
  }
  return ""
}

const Heading = ({
  level,
  children,
  className,
}: {
  level: 2 | 3 | 4
  children: React.ReactNode
  className: string
}) => {
  const id = headingId(childrenToText(children))
  const Tag = `h${level}` as "h2" | "h3" | "h4"
  return (
    <Tag id={id} className={cn("group scroll-mt-20", className)}>
      <a href={`#${id}`} className="no-underline hover:underline">
        {children}
      </a>
    </Tag>
  )
}

type TocEntry = { level: 2 | 3; text: string; id: string }

const buildToc = (content: string): TocEntry[] => {
  const entries: TocEntry[] = []
  let inFence = false
  for (const line of content.split(/\r?\n/)) {
    if (/^```/.test(line)) {
      inFence = !inFence
      continue
    }
    if (inFence) continue
    const m = line.match(/^(#{2,3})\s+(.+?)\s*$/)
    if (!m) continue
    const level = m[1]!.length as 2 | 3
    const text = m[2]!.replace(/`/g, "").trim()
    entries.push({ level, text, id: headingId(text) })
  }
  return entries
}

const useActiveHeading = (ids: string[]): string | null => {
  const idsKey = ids.join("|")
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    if (ids.length === 0) {
      setActive(null)
      return
    }
    setActive((prev) => (prev && ids.includes(prev) ? prev : (ids[0] ?? null)))

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)

    const visible = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }
        const next = ids.find((id) => visible.has(id))
        if (next) setActive((prev) => (prev === next ? prev : next))
      },
      { rootMargin: `-${SCROLLSPY_HEADER_OFFSET}px 0px -70% 0px`, threshold: 0 },
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  return active
}

function TableOfContents({ entries }: { entries: TocEntry[] }) {
  const ids = useMemo(() => entries.map((e) => e.id), [entries])
  const active = useActiveHeading(ids)
  if (entries.length < 2) return null
  return (
    <aside
      aria-label="Table of contents"
      className="hidden xl:block w-56 shrink-0 sticky top-6 self-start max-h-[calc(100vh-3rem)] overflow-y-auto pl-4 border-l border-base"
    >
      <div className="text-xs uppercase tracking-wide text-fg-subtle mb-2">On this page</div>
      <ul className="flex flex-col gap-1 text-sm">
        {entries.map((e) => {
          const isActive = active === e.id
          return (
            <li key={e.id} className={e.level === 3 ? "pl-3" : ""}>
              <a
                href={`#${e.id}`}
                aria-current={isActive ? "location" : undefined}
                className={cn(
                  "block line-clamp-2 leading-snug transition-colors",
                  isActive ? "text-fg font-medium" : "text-fg-subtle hover:text-fg",
                )}
              >
                {e.text}
              </a>
            </li>
          )
        })}
      </ul>
    </aside>
  )
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Heading level={2} className={HEADING_STYLES[2]}>
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level={2} className={HEADING_STYLES[2]}>
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level={3} className={HEADING_STYLES[3]}>
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading level={4} className={HEADING_STYLES[4]}>
      {children}
    </Heading>
  ),
  p: ({ children }) => <p className="my-3">{children}</p>,
  ul: ({ children }) => <ul className="my-3 ml-6 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 ml-6 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="pl-1">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-4 border-base pl-4 italic text-fg-subtle">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-8 border-base" />,
  code: ({ children, ...rest }) => (
    <code className="rounded bg-subtle px-1 py-0.5 text-[0.9em] font-mono" {...rest}>
      {children}
    </code>
  ),
  pre: ({ children }) => {
    const { code, language } = readMarkdownCode(children)
    return <CopyableCode code={code} language={language} className="my-4" />
  },
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-base bg-subtle text-left">{children}</thead>
  ),
  th: ({ children }) => <th className="px-3 py-2 font-medium">{children}</th>,
  td: ({ children }) => <td className="border-b border-base px-3 py-2 align-top">{children}</td>,
  a: ({ href, children }) => {
    if (!href) return <span>{children}</span>
    const internal = resolveDocLink(href)
    if (internal) {
      return (
        <Link to={internal} className="text-fg underline hover:no-underline">
          {children}
        </Link>
      )
    }
    if (href.startsWith("#")) {
      return (
        <a href={href} className="text-fg underline hover:no-underline">
          {children}
        </a>
      )
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fg underline hover:no-underline inline-flex items-baseline gap-0.5"
      >
        {children}
        <ArrowSquareOut
          size={12}
          aria-label="(opens in new tab)"
          className="shrink-0 inline-block translate-y-[1px] text-fg-subtle"
        />
      </a>
    )
  },
}

const MARKDOWN_PLUGINS = [remarkGfm]

const MarkdownBody = memo(function MarkdownBody({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  )
})

const withTheme = (html: string, theme: "light" | "dark"): string =>
  /<html[\s>]/i.test(html) ? html.replace(/<html(?=[\s>])/i, `<html data-theme="${theme}"`) : html

function ArtifactFrame({ doc }: { doc: Doc }) {
  const { resolvedTheme } = useTheme()
  const srcDoc = useMemo(
    () => withTheme(doc.content, resolvedTheme === "dark" ? "dark" : "light"),
    [doc.content, resolvedTheme],
  )
  return (
    <iframe
      title={doc.title}
      srcDoc={srcDoc}
      sandbox={ARTIFACT_SANDBOX}
      className="flex-1 w-full border-0 bg-base"
    />
  )
}

function DocHeader({ doc }: { doc: Doc }) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const lastCommit = doc.history[0]
  const hasMultipleCommits = doc.history.length > 1
  const historyPanelId = `doc-history-${doc.slug}`

  return (
    <header className="mb-8 pb-6 border-b border-base">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">{doc.title}</h1>
        <div className="shrink-0 flex items-center gap-2">
          {doc.sourceUrl && (
            <a
              href={doc.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-base px-2.5 py-1 text-xs text-fg-subtle hover:text-fg hover:bg-subtle"
              title="Open the published artifact on claude.ai"
            >
              <ArrowSquareOut size={12} />
              Open artifact
            </a>
          )}
          <a
            href={`${GITHUB_EDIT_BASE}/${doc.filename}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded border border-base px-2.5 py-1 text-xs text-fg-subtle hover:text-fg hover:bg-subtle"
            title="Edit this doc on GitHub"
          >
            <PencilSimple size={12} />
            Edit on GitHub
          </a>
        </div>
      </div>
      {doc.description && <p className="mt-2 text-fg-subtle">{doc.description}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
        {lastCommit && (
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            aria-expanded={hasMultipleCommits ? historyOpen : undefined}
            aria-controls={hasMultipleCommits ? historyPanelId : undefined}
            className="flex items-center gap-1 hover:text-fg transition-colors cursor-pointer"
          >
            <ClockCounterClockwise size={14} />
            <span>
              Updated {formatDate(lastCommit.date, DateFormatType.FullDateShort)} by{" "}
              {lastCommit.author}
            </span>
            {hasMultipleCommits && (historyOpen ? <CaretUp size={12} /> : <CaretDown size={12} />)}
          </button>
        )}
        {doc.tags.length > 0 && lastCommit && <span>·</span>}
        {doc.tags.map((tag) => (
          <span key={tag} className="rounded bg-subtle px-1.5 py-0.5">
            {tag}
          </span>
        ))}
      </div>

      {historyOpen && doc.history.length > 0 && (
        <div
          id={historyPanelId}
          className="mt-3 rounded-md border border-base bg-subtle/50 overflow-hidden"
        >
          <div className="max-h-64 overflow-y-auto">
            {doc.history.map((commit) => (
              <div
                key={commit.hash}
                className="flex items-baseline gap-3 px-3 py-1.5 text-xs border-b border-base last:border-b-0"
              >
                <a
                  href={`${GITHUB_COMMIT_BASE}/${commit.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-fg-subtle hover:text-fg shrink-0"
                >
                  {commit.hash}
                </a>
                <span className="truncate">{commit.message}</span>
                <span className="ml-auto shrink-0 text-fg-subtle">
                  {commit.author} · {formatDate(commit.date, DateFormatType.FullDateShort)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  )
}

function ArtifactPage({ doc }: { doc: Doc }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-8 pt-10">
        <DocHeader doc={doc} />
      </div>
      <ArtifactFrame doc={doc} />
    </div>
  )
}

function DocPage() {
  const { doc } = Route.useLoaderData()
  if (doc.format === "artifact") return <ArtifactPage doc={doc} />
  return <MarkdownDocPage doc={doc} />
}

function MarkdownDocPage({ doc }: { doc: Doc }) {
  const toc = useMemo(() => buildToc(doc.content), [doc.content])

  return (
    <div className="max-w-6xl mx-auto px-8 py-10 flex gap-8">
      <article className="flex-1 min-w-0 max-w-3xl">
        <DocHeader doc={doc} />

        <div className="text-base leading-relaxed">
          <MarkdownBody content={doc.content} />
        </div>

        {doc.related.length > 0 && (
          <footer className="mt-12 pt-6 border-t border-base">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle mb-3">
              Related
            </h2>
            <ul className="flex flex-col gap-1">
              {doc.related.map((filename) => {
                const related = docsByFilename.get(filename.toLowerCase())
                if (!related) return null
                return (
                  <li key={filename}>
                    <Link
                      to="/internal/docs/$slug"
                      params={{ slug: related.slug }}
                      className="text-fg underline hover:no-underline"
                    >
                      {related.title}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </footer>
        )}
      </article>

      <TableOfContents entries={toc} />
    </div>
  )
}
